import React, { useState, useEffect, useRef, useMemo } from 'react';
import chatbot_icon from '../img/aira-avatar.png';
import bot_icon from '../img/bot_icon.png';
import bot_icon_2 from '../img/bot_icon_2.png';
import ikan_logo from '../img/ikan_logo.png';
import '../css/chatbot.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import {
  initializeSpeechSynthesis,
  loadVoices,
  stopSpeech,
} from '../utils/voiceUtils';
import {
  synthesizeSpeech,
  playAudio,
  stopCurrentAudio,
  pauseCurrentAudio,
  resumeCurrentAudio,
} from '../utils/amazonPolly';

import countriesList from '../data/countriesList.json';

import FAQ from './FAQ';
import { SourcesLink } from './SourcesLink';
import LanguageDropdown from './LangDropdown';
import OTPVerification from './OTPVerification';

// API Base URL - Change this to switch between staging and production
const API_BASE_URL = 'https://chatbotikan.centralindia.cloudapp.azure.com';
// const API_BASE_URL = 'https://ikanchatbotstaging.eastus.cloudapp.azure.com';
//const API_BASE_URL = 'https://ikan-test-setup1.centralindia.cloudapp.azure.com';
const languages = [
  { code: 'en', name: 'English', flag: 'https://flagcdn.com/gb.svg' },
  { code: 'ja', name: 'Japanese', flag: 'https://flagcdn.com/jp.svg' },
  { code: 'de', name: 'German', flag: 'https://flagcdn.com/de.svg' },
  { code: 'fr', name: 'French', flag: 'https://flagcdn.com/fr.svg' },
  { code: 'es', name: 'Spanish', flag: 'https://flagcdn.com/es.svg' },
  { code: 'it', name: 'Italian', flag: 'https://flagcdn.com/it.svg' },
];

const welcomeMessages = {
  en: (name, isNewUser = true) => {
    if (isNewUser) {
      return `Hi ${name}, I'm AIRA — your personal AI Relocation Assistant, how can I assist you today? You can choose from the suggestions below or type in your own question. If you're not sure where to begin, feel free to check out the FAQ section above.\n\nI aim to provide helpful and accurate information, but I might occasionally get things wrong — so it's always a good idea to double-check anything important.\n\nYou can end the chat anytime by typing **exit**.`;
    } else {
      return `Welcome back ${name}! I'm AIRA — your personal AI Relocation Assistant. It's great to see you again! How can I help you today?`
    }
  },

  fr: (name, isNewUser = true) => {
    if (isNewUser) {
      return `Bonjour ${name}, je suis AIRA — votre assistante personnelle en relocalisation. Comment puis-je vous aider aujourd'hui ? Vous pouvez choisir parmi les suggestions ci-dessous ou poser votre propre question. Si vous ne savez pas par où commencer, consultez la section FAQ ci-dessus.\n\nJe m'efforce de fournir des informations utiles et précises, mais je peux parfois me tromper — pensez donc à vérifier les informations importantes.\n\nVous pouvez mettre fin à la discussion à tout moment en tapant **exit**.`;
    } else {
      return `Bon retour ${name} ! Je suis AIRA — votre assistante personnelle en relocalisation. C'est un plaisir de vous revoir ! Comment puis-je vous aider aujourd'hui ? Vous pouvez choisir parmi les suggestions ci-dessous ou poser votre propre question.\n\nJe m'efforce de fournir des informations utiles et précises, mais je peux parfois me tromper — pensez donc à vérifier les informations importantes.\n\nVous pouvez mettre fin à la discussion à tout moment en tapant **exit**.`;
    }
  },

  es: (name, isNewUser = true) => {
    if (isNewUser) {
      return `Hola ${name}, soy AIRA — tu asistente personal de reubicación. ¿Cómo puedo ayudarte hoy? Puedes elegir entre las sugerencias de abajo o escribir tu propia pregunta. Si no sabes por dónde empezar, consulta la sección de preguntas frecuentes.\n\nIntento proporcionar información útil y precisa, pero a veces puedo equivocarme, así que es buena idea verificar cualquier cosa importante.\n\nPuedes terminar el chat en cualquier momento escribiendo **exit**.`;
    } else {
      return `¡Bienvenido de vuelta ${name}! Soy AIRA — tu asistente personal de reubicación. ¡Es genial verte de nuevo! ¿Cómo puedo ayudarte hoy? Puedes elegir entre las sugerencias de abajo o escribir tu propia pregunta.\n\nIntento proporcionar información útil y precisa, pero a veces puedo equivocarme, así que es buena idea verificar cualquier cosa importante.\n\nPuedes terminar el chat en cualquier momento escribiendo **exit**.`;
    }
  },

  de: (name, isNewUser = true) => {
    if (isNewUser) {
      return `Hallo ${name}, ich bin AIRA – deine persönliche Umzugsassistentin. Wie kann ich dir heute helfen? Du kannst unten eine der vorgeschlagenen Fragen auswählen oder deine eigene eingeben. Wenn du nicht weißt, wo du anfangen sollst, schau dir den FAQ-Bereich oben an.\n\nIch bemühe mich, hilfreiche und genaue Informationen bereitzustellen, aber gelegentlich kann ich mich irren – deshalb solltest du wichtige Informationen immer überprüfen.\n\nDu kannst den Chat jederzeit beenden, indem du **exit** eingibst.`;
    } else {
      return `Willkommen zurück ${name}! Ich bin AIRA – deine persönliche Umzugsassistentin. Schön, dich wiederzusehen! Wie kann ich dir heute helfen? Du kannst unten eine der vorgeschlagenen Fragen auswählen oder deine eigene eingeben.\n\nIch bemühe mich, hilfreiche und genaue Informationen bereitzustellen, aber gelegentlich kann ich mich irren – deshalb solltest du wichtige Informationen immer überprüfen.\n\nDu kannst den Chat jederzeit beenden, indem du **exit** eingibst.`;
    }
  },

  ja: (name, isNewUser = true) => {
    if (isNewUser) {
      return `こんにちは ${name} さん。私は AIRA、あなたのパーソナルAI転居アシスタントです。本日どのようにお手伝いできますか？下の提案から選ぶか、自分で質問を入力してください。どこから始めればよいかわからない場合は、上部のFAQをご確認ください。\n\n役立つ正確な情報を提供するよう努めていますが、間違うこともあるかもしれません。重要な情報は必ず確認してください。\n\nチャットを終了したい場合は、いつでも「**exit**」と入力してください。`;
    } else {
      return `おかえりなさい ${name} さん！私は AIRA、あなたのパーソナルAI転居アシスタントです。またお会いできて嬉しいです！本日どのようにお手伝いできますか？下の提案から選ぶか、自分で質問を入力してください。\n\n役立つ正確な情報を提供するよう努めていますが、間違うこともあるかもしれません。重要な情報は必ず確認してください。\n\nチャットを終了したい場合は、いつでも「**exit**」と入力してください。`;
    }
  },

  it: (name, isNewUser = true) => {
    if (isNewUser) {
      return `Ciao ${name}, sono AIRA — il tuo assistente personale per la re-localizzazione. Come posso aiutarti oggi? Puoi scegliere tra le seguenti domande o inserire la tua domanda. Se non sai da dove iniziare, consulta la sezione FAQ sopra.\n\nMi impegno a fornire informazioni utili e accurate, ma potrei occasionalmente sbagliare — quindi è sempre una buona idea controllare le informazioni importanti.\n\nPuoi terminare il chat in qualsiasi momento digitando **exit**.`;
    } else {
      return `Bentornato ${name}! Sono AIRA — il tuo assistente personale per la re-localizzazione. È bello rivederti! Come posso aiutarti oggi? Puoi scegliere tra le seguenti domande o inserire la tua domanda.\n\nMi impegno a fornire informazioni utili e accurate, ma potrei occasionalmente sbagliare — quindi è sempre una buona idea controllare le informazioni importanti.\n\nPuoi terminare il chat in qualsiasi momento digitando **exit**.`;
    }
  },
};

// Function to generate starter questions dynamically based on country and language
const getStarterQuestions = (countryName, languageCode, includeIKANServices = false) => {
  // Find country data from countriesList
  let countryData = countriesList.find(
    (country) => country.name.toLowerCase() === countryName?.toLowerCase()
  );

  // If country not found, fallback to India from JSON
  if (!countryData) {
    countryData = countriesList.find(
      (country) => country.name.toLowerCase() === 'india'
    );
  }

  // Get cities from country data (will be India's cities if country not found)
  const cities = countryData?.cities || [];
  const country = countryData?.name || countryName || 'India';
  
  // Use first 3 cities, or repeat if less than 3 cities available
  const city1 = cities[0] || '';
  const city2 = cities[1] || cities[0] || '';
  const city3 = cities[2] || cities[0] || '';

  // Question templates for each language
  const questionTemplates = {
    en: {
      ikanServices: 'Tell me about IKAN services',
      home: `How do I find a home in ${city1}?`,
      schools: `Can you recommend international schools in ${city2}`,
      visa: `What visa do I need to work in ${country}?`,
      moving: 'I need information on moving my household goods and pets',
      weather: `Current weather in ${city3}`,
    },
    ja: {
      ikanServices: 'IKANのサービスについて教えてください',
      home: `${city1}で家を見つけるにはどうすればいいですか？`,
      schools: `${city2}のインターナショナルスクールをおすすめしてもらえますか？`,
      visa: `${country}で働くにはどのビザが必要ですか？`,
      moving: '家財道具やペットの引越しについての情報が必要です',
      weather: `${city3}の現在の天気`,
    },
    de: {
      ikanServices: 'Erzählen Sie mir von den IKAN-Dienstleistungen',
      home: `Wie finde ich ein Zuhause in ${city1}?`,
      schools: `Können Sie internationale Schulen in ${city2} empfehlen?`,
      visa: `Welches Visum benötige ich, um in ${country} zu arbeiten?`,
      moving: 'Ich benötige Informationen zum Umzug meiner Haushaltswaren und Haustiere',
      weather: `Aktuelles Wetter in ${city3}`,
    },
    fr: {
      ikanServices: 'parlez-moi des services ikan',
      home: `Comment puis-je trouver un logement à ${city1} ?`,
      schools: `Pouvez-vous recommander des écoles internationales à ${city2} ?`,
      visa: `Quel visa faut-il pour travailler en ${country} ?`,
      moving: "J'ai besoin d'informations pour déménager mes biens et mes animaux de compagnie",
      weather: `Météo actuelle à ${city3}`,
    },
    es: {
      ikanServices: 'Cuéntame sobre los servicios de IKAN',
      home: `¿Cómo puedo encontrar una casa en ${city1}?`,
      schools: `¿Puedes recomendar escuelas internacionales en ${city2}?`,
      visa: `¿Qué visa necesito para trabajar en ${country}?`,
      moving: 'Necesito información sobre cómo trasladar mis pertenencias y mascotas',
      weather: `Clima actual en ${city3}`,
    },
    it: {
      ikanServices: 'Parlami dei servizi di IKAN',
      home: `Come posso trovare un appartamento a ${city1}?`,
      schools: `Puoi consigliarmi scuole internazionali a ${city2}?`,
      visa: `Quale viso serve per lavorare in ${country}?`,
      moving: 'Ho bisogno di informazioni su come trasferire i miei beni e animali domestici',
      weather: `Situazione meteo a ${city3}`,
    },
  };

  const templates = questionTemplates[languageCode] || questionTemplates.en;
  
  // Build questions array
  const questions = [];
  
  // Add IKAN services question only if includeIKANServices is true
  if (includeIKANServices) {
    questions.push(templates.ikanServices);
  }
  
  // Add other questions
  questions.push(
    templates.home,
    templates.schools,
    templates.visa,
    templates.moving,
    templates.weather
  );

  return questions;
};

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    country: 'India',
    isManagingRelocation: false,
  });
  const [errors, setErrors] = useState({ name: '', email: '', country: '' });
  const [showUserInfoPopup, setShowUserInfoPopup] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: 'bot',
      text: `Hi, I'm AIRA — IKAN's personal AI Assistant. How can I help you today?
While I strive to be accurate, it's always wise to double-check important details.`,
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [showFAQ, setShowFAQ] = useState(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);
  const [currentSpeechText, setCurrentSpeechText] = useState('');
  const [currentPosition, setCurrentPosition] = useState(0);
  const [currentVoice, setCurrentVoice] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const [isNewUser, setIsNewUser] = useState(true);
  // Dropdown-related state
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');

  const [showStarterQuestions, setShowStarterQuestions] = useState(() => {
    const stored = sessionStorage.getItem('showStarterQuestions');
    return stored === null ? true : JSON.parse(stored);
  });
  const [selectedLang, setSelectedLang] = useState(() => {
    const savedLangCode = sessionStorage.getItem('preferredLang');
    const defaultLang = languages.find((l) => l.code === 'en') || languages[0];

    if (savedLangCode) {
      const lang = languages.find((l) => l.code === savedLangCode);
      return lang || defaultLang;
    }

    return defaultLang;
  });

  const speechSynthesis = window.speechSynthesis;

  const chatContainerRef = useRef(null);
  // Dropdown-related refs
  const countryDropdownRef = useRef(null);
  const countrySearchInputRef = useRef(null);

  // Dropdown-related useMemo hooks
  const countryOptions = useMemo(() => {
    const names = countriesList.map((country) => country.name);
    const uniqueNames = Array.from(new Set(names));
    return uniqueNames.sort((a, b) => a.localeCompare(b));
  }, []);

  const filteredCountries = useMemo(() => {
    if (!countrySearchTerm.trim()) {
      return countryOptions;
    }
    return countryOptions.filter((countryName) =>
      countryName.toLowerCase().includes(countrySearchTerm.toLowerCase())
    );
  }, [countrySearchTerm, countryOptions]);
  const scrollToLatestMessage = () => {
    if (chatContainerRef.current) {
      const botMessages = chatContainerRef.current.querySelectorAll('.bot-text');
      if (botMessages.length > 0) {
        const latestBotMessage = botMessages[botMessages.length - 1];
        const userMessages =
          chatContainerRef.current.querySelectorAll('.user-text');
        const lastUserMessage = userMessages[userMessages.length - 1];

        if (lastUserMessage) {
          lastUserMessage.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        } else {
          latestBotMessage.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }
    }
  };

  
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  const validateName = (name) => {
    const nameRegex = /^[a-zA-Z\s]+$/;
    if (!name.trim()) {
      return 'Name is required';
    }
    return '';
  };


  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      return 'Email is required';
    }
    if (!emailRegex.test(email.trim())) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validateCountry = (country) => {
    if (!country || !country.trim()) {
      return 'Destination Country is required';
    }
    return '';
  };

  useEffect(() => {
    sessionStorage.setItem(
      'showStarterQuestions',
      JSON.stringify(showStarterQuestions)
    );
  }, [showStarterQuestions]);

  // Dropdown-related useEffect hooks
  useEffect(() => {
    if (!isCountryDropdownOpen) {
      return;
    }

    const handleClickOutside = (event) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target)
      ) {
        setIsCountryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCountryDropdownOpen]);

  useEffect(() => {
    if (isCountryDropdownOpen && countrySearchInputRef.current) {
      countrySearchInputRef.current.focus();
    }
  }, [isCountryDropdownOpen]);
  
  useEffect(() => {
    if (chatContainerRef.current && !isTyping) {
      const botMessages =
        chatContainerRef.current.querySelectorAll('.bot-text');
      if (botMessages.length > 0) {
        setTimeout(() => {
          const latestBotMessage = botMessages[botMessages.length - 1];
          const userMessages =
            chatContainerRef.current.querySelectorAll('.user-text');
          const lastUserMessage = userMessages[userMessages.length - 1];

          if (lastUserMessage) {
            lastUserMessage.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest',
            });
          } else {
            latestBotMessage.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest'
            });
          }
        }, 100);
      } else {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }
  }, [messages, isTyping]);

  useEffect(() => {
    // Initialize speech synthesis
    if (initializeSpeechSynthesis()) {
      loadVoices();
    }
    setIsFirstLoad(false);
  }, []);

  useEffect(() => {
    const storedUserInfo = JSON.parse(sessionStorage.getItem('chatUserInfo'));
    const storedMessages = JSON.parse(sessionStorage.getItem('chatMessages'));
    if (storedUserInfo) {
      setUserInfo(storedUserInfo);
      setShowChat(true);
    }
    if (storedMessages) {
      setMessages(storedMessages);
    }
  }, []);

  useEffect(() => {
    if (showChat) {
      sessionStorage.setItem('chatUserInfo', JSON.stringify(userInfo));
    }
    sessionStorage.setItem('chatMessages', JSON.stringify(messages));
  }, [userInfo, messages]);

  useEffect(() => {
    if (showChat && messages.length > 0) {
      setCurrentVoice(`${selectedLang.name} (${selectedLang.code})`);
    }
  }, [selectedLang, showChat]);

  useEffect(() => {
    if (!userInfo?.name || messages.length === 0) return;

    // Find index of the first bot message (welcome)
    const welcomeIndex = messages.findIndex((m) => m.sender === 'bot');

    if (welcomeIndex === -1) return;

    const newWelcomeMessage =
      welcomeMessages[selectedLang.code]?.(formatName(userInfo.name), false) ||
      welcomeMessages.en(formatName(userInfo.name), false);

    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages];
      updatedMessages[welcomeIndex] = {
        ...updatedMessages[welcomeIndex],
        text: newWelcomeMessage,
      };
      return updatedMessages;
    });
  }, [selectedLang]);

  const formatName = (name) => {
    if (!name) return '';
    return name
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Dropdown-related handlers
  const handleCountrySelect = (countryName) => {
    setUserInfo((prevUserInfo) => ({ ...prevUserInfo, country: countryName }));
    setErrors((prevErrors) => ({ ...prevErrors, country: '' }));
    setCountrySearchTerm('');
    setIsCountryDropdownOpen(false);
  };

  const handleCountryFieldClick = () => {
    setCountrySearchTerm('');
    setIsCountryDropdownOpen((prevOpen) => !prevOpen);
  };

  const handleStart = async () => {
    let newErrors = { name: '', email: '', country: '' };
    let hasError = false;

    const nameError = validateName(userInfo.name);
    if (nameError) {
      newErrors.name = nameError;
      hasError = true;
    }

    const emailError = validateEmail(userInfo.email);
    if (emailError) {
      newErrors.email = emailError;
      hasError = true;
    }

    const countryError = validateCountry(userInfo.country);
    if (countryError) {
      newErrors.country = countryError;
      hasError = true;
    }

    setErrors(newErrors);

    if (!hasError && acceptedTerms) {
      setIsStarting(true);
      try {
        localStorage.setItem('userFormData', JSON.stringify(userInfo));
        localStorage.removeItem('otpResponse');
        setShowOTP(true);
        const response = await fetch(
          `${API_BASE_URL}/send-otp`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: userInfo.email
            }),
          }
        );
    
        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('otpResponse', JSON.stringify(data));
          setShowOTP(false);
          setTimeout(() => setShowOTP(true), 100);
        }
      } finally {
        setIsStarting(false);
      }
    } else {
      setIsStarting(false);
    }
    
  };

  const handleOTPBack = () => {
    setShowOTP(false);
    setErrors({ name: '', email: '', country: '' });
    setAcceptedTerms(false);
    localStorage.removeItem('userFormData');
    localStorage.removeItem('otpResponse');
    setUserInfo({
      name: '',
      email: '',
      country: 'India',
      isManagingRelocation: false,
    });
    setIsCountryDropdownOpen(false);
    setCountrySearchTerm('');
  };

  const handleOTPVerify = async (otpString) => {
    try {
      const storedFormData = JSON.parse(localStorage.getItem('userFormData'));
      
      if (!storedFormData) {
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/create-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: storedFormData.name,
            email: storedFormData.email,
            country: storedFormData.country,
            isManagingRelocation: storedFormData.isManagingRelocation,
            otp: otpString
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();        
        if (data.otp_validated && data.user_id) {
          sessionStorage.setItem('user_id', data.user_id);
          // localStorage.removeItem('userFormData');
          return data;
        } else if (data.otp_validated === false) {
          throw new Error('Incorrect OTP entered');
        } else {
          throw new Error('Verification failed. Please try again.');
        }
      } else {
        throw new Error('API request failed. Please try again.');
      }
    } catch (error) {
      throw error;
    }
  };

  const handleOTPVerificationSuccess = (apiResponseData) => {
    setShowOTP(false);
    setShowChat(true);
    const storedFormData = JSON.parse(localStorage.getItem('userFormData'));
    if (storedFormData) {
      const formatName = (name) => {
        if (!name) return '';
        return name
          .trim()
          .split(/\s+/)
          .map(
            (word) =>
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(' ');
      };

      // Check if user is new or existing based on API response
      const isNewUserFromAPI = apiResponseData?.new_user === "1";
      setIsNewUser(isNewUserFromAPI);
      
      const welcomeMessage =
        welcomeMessages[selectedLang.code]?.(formatName(storedFormData.name), isNewUserFromAPI) ||
        welcomeMessages.en(formatName(storedFormData.name), isNewUserFromAPI); // fallback to English

      // Prepare initial messages: welcome + recent conversations (if any)
      const initialMessages = [
        {
          sender: 'bot',
          text: welcomeMessage,
          preLine: true,
        },
      ];

      // Helper to normalize recent conversations into message list
      const normalizeRecentConversations = (recent) => {
        const normalized = [];
        if (!Array.isArray(recent) || recent.length === 0) return normalized;

        // Case A: array of role/content messages
        if (recent.every((m) => m && typeof m === 'object' && ('role' in m) && (('content' in m) || ('message' in m) || ('text' in m)))) {
          // take last 6 messages (approx last 3 exchanges)
          const slice = recent.slice(-6);
          slice.forEach((m) => {
            const role = (m.role || '').toLowerCase();
            const content = m.content ?? m.message ?? m.text ?? '';
            if (!content) return;
            if (role === 'user') normalized.push({ sender: 'user', text: content });
            if (role === 'assistant' || role === 'bot' || role === 'ai') normalized.push({ sender: 'bot', text: content });
          });
          return normalized;
        }

        // Case B: array of pairs like { user_message, ai_message } or similar
        const lastThree = recent.slice(-3);
        lastThree.forEach((item) => {
          if (!item || typeof item !== 'object') return;
          const userText = item.user_message ?? item.user ?? item.question ?? item.userMsg ?? item.prompt ?? item.query ?? '';
          const botText = item.ai_message ?? item.ai_response ?? item.answer ?? item.response ?? item.ai ?? item.bot_message ?? item.bot ?? item.assistant_message ?? item.assistant ?? item.reply ?? item.botMsg ?? item.message ?? '';
          if (userText) normalized.push({ sender: 'user', text: String(userText) });
          if (botText) normalized.push({ sender: 'bot', text: String(botText) });
        });
        return normalized;
      };

      const recentFromAPI = apiResponseData?.recent_conversations;
      const recentMessages = normalizeRecentConversations(recentFromAPI);

      // If we have recent conversations, hide the initial welcome message visually
      if (recentMessages.length > 0 && initialMessages[0]) {
        initialMessages[0] = { ...initialMessages[0], hidden: true };
      }

      setMessages(initialMessages.concat(recentMessages));

      // Show starter questions only when there are no recent conversations
      if (recentMessages.length === 0) {
        setShowStarterQuestions(true);
      } else {
        setShowStarterQuestions(false);
      }

      // Speak the welcome message only if there are no recent conversations
      const hasRecent = Array.isArray(recentMessages) && recentMessages.length > 0;
      if (!hasRecent) {
        setTimeout(() => {
          if (!isPaused) {
            if (isPaused) {
              setIsPaused(false);
            }
            try {
              const audioContext = new (window.AudioContext || window.webkitAudioContext)();
              if (audioContext.state === 'suspended') {
                audioContext.resume().then(() => {
                }).catch(error => {
                });
              }
            } catch (audioError) {
            }
            handleSpeech(welcomeMessage).catch(error => {
              setTimeout(() => {
                handleSpeech(welcomeMessage);
              }, 500);
            });
          } else {
          }
        }, 100);
      }
      localStorage.removeItem('userFormData');
    }
  };

  const handleOTPResend = async () => {
    try {
      const storedFormData = JSON.parse(localStorage.getItem('userFormData'));
      
      if (!storedFormData) {
        console.error('No form data found in localStorage');
        return;
      }
      const response = await fetch(
        `${API_BASE_URL}/send-otp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: storedFormData.email
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.otp_sent) {
          return Promise.resolve();
        } else {
          throw new Error('Failed to resend OTP');
        }
      } else {
        throw new Error('Failed to resend OTP');
      }
      
    } catch (error) {
      throw error;
    }
  };

  const handleVoiceButtonClick = () => {
    if (!isPaused) {
      // Mute current speech
      stopSpeech();
      pauseCurrentAudio(); // Pause the current audio
      setIsPaused(true);
      setIsSpeaking(false);
    } else {
      // Unmute and resume
      setIsPaused(false);
      resumeCurrentAudio(); // Resume from where it was paused
    }
  };

  const handleSpeech = async (text) => {
    // Don't proceed if muted
    if (isPaused) {
      return;
    }

    // Clean and format the text for speech
    const cleanText = text
      // Convert markdown-style links to just the visible text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Remove URLs (if any outside markdown)
      .replace(/https?:\/\/[^\s]+|www\.[^\s]+/gi, '')
      // Remove emojis
      .replace(/\p{Extended_Pictographic}|\u200D/gu, '')
      // Remove SSML-sensitive characters
      .replace(/[&<>]/g, '')
      .trim();

    // Create SSML with IKAN pronunciation and proper breaks
    const ssmlText = `<speak>
      ${cleanText
        .replace(/IKAN/gi, 'I CAN')
        .replace(/AIRA/gi, 'AY-rah')
        .replace(/:/g, '.')
        .replace(/-/g, ' ')}
    </speak>`;

    // Reset states for new responses
    setIsSpeaking(false);
    setCurrentPosition(0);
    setCurrentSpeechText(cleanText);

    // Start new speech
    stopSpeech();
    stopCurrentAudio(); // Stop any existing audio
    setIsSpeaking(true);

    try {
      const audioStream = await synthesizeSpeech(
        ssmlText,
        true,
        selectedLang.code
      ); // Pass language code
      setCurrentVoice(`${selectedLang.name} (${selectedLang.code})`);
      if (!isPaused) {
        await playAudio(audioStream);
      } else {
      }
      setIsSpeaking(false);
      setCurrentPosition(0);
    } catch (error) {
      setIsSpeaking(false);
      setCurrentPosition(0);
    }
  };

  const handleSpeechWithLanguage = async (text, languageCode) => {
    if (isPaused) {
      return;
    }
    const cleanText = text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/https?:\/\/[^\s]+|www\.[^\s]+/gi, '')
      .replace(/\p{Extended_Pictographic}|\u200D/gu, '')
      .replace(/[&<>]/g, '')
      .trim();

    const ssmlText = `<speak>
      ${cleanText
        .replace(/IKAN/gi, 'I CAN')
        .replace(/AIRA/gi, 'AY-rah')
        .replace(/:/g, '.')
        .replace(/-/g, ' ')}
    </speak>`;

    setIsSpeaking(false);
    setCurrentPosition(0);
    setCurrentSpeechText(cleanText);
    stopSpeech();
    stopCurrentAudio();
    setIsSpeaking(true);

    try {
      const audioStream = await synthesizeSpeech(ssmlText, true, languageCode);
      const lang = languages.find((l) => l.code === languageCode);
      setCurrentVoice(`${lang?.name || languageCode} (${languageCode})`);
      if (!isPaused) {
        await playAudio(audioStream);
      }
      setIsSpeaking(false);
      setCurrentPosition(0);
    } catch (error) {
      console.error('Speech error:', error);
      setIsSpeaking(false);
      setCurrentPosition(0);
    }
  };

  const sendMessage = async () => {
    setShowStarterQuestions(false);
    if (inputMessage.trim()) {
      // Cancel any ongoing speech
      stopSpeech();
      stopCurrentAudio(); // Stop and clear any stored audio
      setIsSpeaking(false);

      const newMessage = { sender: 'user', text: inputMessage };
      setMessages([...messages, newMessage]);
      setInputMessage('');
      setIsTyping(true);
      
      // Scroll to bottom for user message
      setTimeout(() => scrollToBottom(), 50);

      const userId = sessionStorage.getItem('user_id');

      try {
        const response = await fetch(
          `${API_BASE_URL}/chat`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              message: inputMessage,
              user_id: userId,
              user_lang: selectedLang.code,
              country: userInfo.country,
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          const formattedText = data.reply.replace(/[#*]/g, ''); // Remove both hashtags and asterisks
           // Handle multiple message blocks if available
           if (data.message_blocks && data.message_blocks.length > 1) {
            // Add each block as a separate message
            const newMessages = data.message_blocks.map((block, index) => ({
              sender: 'bot',
              text: block,
              sources: index === data.message_blocks.length - 1 ? (data.sources || []) : [], // Only add sources to last block
            }));
            setMessages((prevMessages) => [...prevMessages, ...newMessages]);
          } else {
            // Single message (fallback to original behavior)
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                sender: 'bot',
                text: data.reply,
                sources: data.sources || [],
              },
            ]);
          }
          // Scroll to the beginning of the latest bot message
          setTimeout(() => scrollToLatestMessage(), 100);
          // Only start speech if not muted
          if (!isPaused) {
            handleSpeech(formattedText);
          }
        } else {
          setMessages((prevMessages) => [
            ...prevMessages,
            { sender: 'bot', text: 'Error: Unable to fetch response' },
          ]);
          setTimeout(() => scrollToLatestMessage(), 100);
        }
      } catch (error) {
        setMessages((prevMessages) => [
          ...prevMessages,
          { sender: 'bot', text: 'Error: API request failed' },
        ]);
        setTimeout(() => scrollToLatestMessage(), 100);
      } finally {
        setIsTyping(false);
      }
    }
  };

  const handleFAQQuestionSelect = async (question) => {
    setShowStarterQuestions(false);
    // Cancel any ongoing speech
    stopSpeech();
    stopCurrentAudio(); // Stop and clear any stored audio
    setIsSpeaking(false);

    const newMessage = { sender: 'user', text: question };
    setMessages((prevMessages) => [...prevMessages, newMessage]);
    setShowFAQ(false); // close FAQ popup
    
    // Scroll to bottom for user message
    setTimeout(() => scrollToBottom(), 50);

    const userId = sessionStorage.getItem('user_id');
    setIsTyping(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: question,
            user_id: userId,
            user_lang: selectedLang.code,
            country: userInfo.country,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const formattedText = data.reply.replace(/[#*]/g, ''); // Remove both hashtags and asterisks
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            sender: 'bot',
            text: data.reply,
            sources: data.sources || [], // ⬅️ new addition
          },
        ]);
        // Scroll to the beginning of the latest bot message
        setTimeout(() => scrollToLatestMessage(), 100);
        // Only start speech if not muted
        if (!isPaused) {
          handleSpeech(formattedText);
        }
      } else {
        setMessages((prevMessages) => [
          ...prevMessages,
          { sender: 'bot', text: 'Error: Unable to fetch response' },
        ]);
        setTimeout(() => scrollToLatestMessage(), 100);
      }
    } catch (error) {
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: 'bot', text: 'Error: API request failed' },
      ]);
      setTimeout(() => scrollToLatestMessage(), 100);
    } finally {
      setIsTyping(false);
    }
  };

  const handleLanguageChange = (langCode) => {
    const lang = languages.find((l) => l.code === langCode);
    if (lang) {
      stopSpeech();
      stopCurrentAudio();
      setIsSpeaking(false);

      setSelectedLang(lang);
      sessionStorage.setItem('preferredLang', lang.code);
      if (messages.length > 0 && showChat && !isPaused) {
        const lastBotMessage = messages
          .slice()
          .reverse()
          .find((msg) => msg.sender === 'bot');

        if (lastBotMessage) {
          const cleanText = lastBotMessage.text
            .replace(/[#*]/g, '')
            .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
            .replace(/https?:\/\/[^\s]+|www\.[^\s]+/gi, '')
            .replace(/\p{Extended_Pictographic}|\u200D/gu, '')
            .replace(/[&<>]/g, '')
            .trim();
          handleSpeechWithLanguage(cleanText, langCode);
        }
      }
    }

    console.log('selected language', selectedLang);
  };

  const translatedStarterQuestions = (() => {
    // Check if country is India to include IKAN services question
    const isIndia = userInfo.country && userInfo.country.toLowerCase().trim() === 'india';
    
    // Generate starter questions dynamically based on selected country and language
    return getStarterQuestions(
      userInfo.country,
      selectedLang.code,
      isIndia
    );
  })();

  return (
    <div className="fixed bottom-[-6px] right-6 z-50">
      <button className="focus:outline-none pointer-events-auto" onClick={() => setIsOpen(!isOpen)}>
        <img
          src={chatbot_icon}
          alt="Chatbot Icon"
          className="w-[92px] h-[92px]"
        />
        {!isOpen && (
          <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-[#164451] text-white text-sm rounded-lg shadow-lg whitespace-nowrap z-50">
            Hi, I'm <strong>AIRA.</strong><br/>How can I help you?
            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-[#164451]"></div>
          </div>
        )}
      </button>
      {isOpen && (
        // <div className="custom-height fixed pointer-events-none bottom-20 right-6 w-[90%] max-w-[650px] md:w-[600px] md:h-[500px] l:w-[650px] 2xl:w-[650px] h-[550px]  xl:h-[500px] 2xl:h-[600px] shadow-2xl bg-[#FEFEFC] rounded-2xl border border-[#949699] animate-fadeIn flex flex-col">
        <div
        className={
          isOpen
            ? "custom-height fixed bottom-20 right-6 w-[90%] max-w-[650px] md:w-[600px] md:h-[500px] l:w-[650px] 2xl:w-[650px] h-[550px] xl:h-[500px] 2xl:h-[600px] shadow-2xl bg-[#FEFEFC] rounded-2xl border border-[#949699] animate-fadeIn flex flex-col"
            : "custom-height fixed bottom-20 right-6 max-w-[92px] max-h-[92px] shadow-2xl bg-[#FEFEFC] rounded-2xl border border-[#949699] animate-fadeIn flex flex-col"
        }
      >
          {/* Header */}
          <div className="flex items-center justify-between p-3 gradient-bg rounded-t-2xl">
            <div className="flex items-end">
              <img src={ikan_logo} alt="Logo" className="ikan-logo" />
              <span className="beta-text px-2">Beta</span>
            </div>
            <div className="flex items-center space-x-3">
              {showChat && (
                <>
                  {userInfo.country && userInfo.country.toLowerCase().trim() === 'india' && (
                    <div
                      className="px-3 py-1 faq-button cursor-pointer"
                      onClick={() => setShowFAQ((prev) => !prev)}
                    >
                      FAQs
                    </div>
                  )}
                  <div className="w-[26px] h-[26px] flex items-center justify-center rounded-full border-2 border-[#164451]">
                    <i
                      className="fa-solid fa-user text-gray-700 cursor-pointer"
                      onClick={() => setShowUserInfoPopup(true)}
                    ></i>
                  </div>
                </>
              )}
              {!showChat && !showOTP && (
                <div>
                  <LanguageDropdown
                    languages={languages}
                    onLanguageChange={handleLanguageChange}
                  />
                </div>
              )}
              <i
                className="fa-solid fa-chevron-down cursor-pointer text-gray-700"
                onClick={() => setIsOpen(false)}
              ></i>
            </div>
          </div>
          {/* Showing FAQ inside Chat */}
          {/* {showFAQ && (
            <div className='faq-wrapper p-4 overflow-y-auto'>
              <FAQ onClose={() => setShowFAQ(false)} />
            </div>
          )} */}

          {showFAQ && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white pt-[20px] pb-[16px] rounded-lg shadow-lg w-11/12 max-w-[600px] h-[70vh] z-[1000] border border-[#164451] overflow-y-auto custom-scrollbar">
              <div className="flex justify-between px-4 items-center mb-4">
                <h3 className="faq-title">Frequently Asked Questions</h3>
                <button className="" onClick={() => setShowFAQ(false)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 1.5C6.15 1.5 1.5 6.15 1.5 12C1.5 17.85 6.15 22.5 12 22.5C17.85 22.5 22.5 17.85 22.5 12C22.5 6.15 17.85 1.5 12 1.5ZM16.05 17.25L12 13.2L7.95 17.25L6.75 16.05L10.8 12L6.75 7.95L7.95 6.75L12 10.8L16.05 6.75L17.25 7.95L13.2 12L17.25 16.05L16.05 17.25Z"
                      fill="#FF0000"
                    />
                  </svg>
                  {/* <i className="fa-solid fa-xmark text-[24px] text-[#FFFFFF]"></i> */}
                </button>
              </div>
              <FAQ
                onClose={() => setShowFAQ(false)}
                onSelectQuestion={handleFAQQuestionSelect}
                selectedLang={selectedLang}
              />
            </div>
          )}
          {/* User Info Popup */}
          {showUserInfoPopup && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white pt-[27px] pb-[16px] px-[12px] rounded-lg shadow-lg w-11/12 border border-2 border-[#164451] z-[1001]">
              <h3 className="intro-heading mb-1">Let's Talk</h3>
              <p className="sub-text mb-[22px]">
                Provide some basic information to help us begin our
                conversation.
              </p>

              <label className="label-text mb-1.5">Name</label>
              <input
                type="text"
                className="w-full py-1 px-3 mb-4 user-input-field border rounded-lg focus:ring-1 focus:ring-[#134550]"
                value={userInfo.name}
                // onChange={(e) =>
                //   setUserInfo({ ...userInfo, name: e.target.value })
                // }
                // required
                disabled
              />

              <label className="label-text mb-1.5">Email</label>
              <input
                type="email"
                className="w-full py-1 px-3 mb-4 user-input-field border rounded-lg focus:ring-1 focus:ring-[#134550]"
                value={userInfo.email}
                // onChange={(e) =>
                //   setUserInfo({ ...userInfo, email: e.target.value })
                // }
                // required
                disabled
              />

              <label className="label-text mb-1.5">Country</label>
              <input
                type="text"
                className="w-full py-1 px-3 mb-4 user-input-field border rounded-lg focus:ring-1 focus:ring-[#134550]"
                value={userInfo.country}
                // onChange={(e) =>
                //   setUserInfo({ ...userInfo, country: e.target.value })
                // }
                // required
                disabled
              />

              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  className="mr-2 focus:outline-1"
                  checked={acceptedTerms}
                  // onChange={() => setAcceptedTerms(!acceptedTerms)}
                  disabled
                />
                <span className="check-box-text">
                  I accept{' '}
                  <span className="check-box-link cursor-pointer" onClick={() => setShowTermsPopup(true)}>Terms & Conditions</span> and{' '}
                  <span className="check-box-link cursor-pointer" onClick={() => setShowPrivacyPopup(true)}>Privacy Policy</span>
                </span>
              </div>

              <button
                className={`w-full p-2.5 rounded-lg start-button transition duration-300 ${
                  userInfo.name &&
                  userInfo.email &&
                  userInfo.country &&
                  acceptedTerms
                    ? 'bg-[#164451]'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
                onClick={() => setShowUserInfoPopup(false)}
                disabled={
                  !(
                    userInfo.name &&
                    userInfo.email &&
                    userInfo.country &&
                    acceptedTerms
                  )
                }
              >
                CLOSE
              </button>
            </div>
          )}
          
          {/* Terms & Conditions Popup */}
          {showTermsPopup && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white pt-[20px] pb-[16px] rounded-lg shadow-lg w-11/12 max-w-[600px] h-[70vh] z-[1002] border border-[#164451] overflow-y-auto custom-scrollbar">
              <div className="flex justify-between px-4 items-center mb-4">
                <div></div>
                <button className="" onClick={() => setShowTermsPopup(false)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 1.5C6.15 1.5 1.5 6.15 1.5 12C1.5 17.85 6.15 22.5 12 22.5C17.85 22.5 22.5 17.85 22.5 12C22.5 6.15 17.85 1.5 12 1.5ZM16.05 17.25L12 13.2L7.95 17.25L6.75 16.05L10.8 12L6.75 7.95L7.95 6.75L12 10.8L16.05 6.75L17.25 7.95L13.2 12L17.25 16.05L16.05 17.25Z"
                      fill="#FF0000"
                    />
                  </svg>
                </button>
              </div>
              <div className="px-4">
                <div className="terms-content">
                  <h4 className="text-lg font-semibold mb-3">Terms and Conditions for using IKANTEK's Chatbot - AIRA</h4>
                  <p className="text-sm text-gray-600 mb-4"><strong>Effective Date:</strong> June 15th 2025</p>
                  
                  <p className="mb-3">
                    Welcome to IKANTEK Solutions LLP ("we," "our," or "us"). These Terms and Conditions ("Terms") govern your use of AIRA, our AI chatbot services ("Service"), whether accessed via website, mobile application, messaging platform, or any other medium.
                  </p>
                  
                  <p className="mb-3">
                    By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, please do not use the Service.
                  </p>
                  
                  <p className="mb-3">
                    <strong>1. Service Overview</strong><br/>
                    AIRA is an AI-powered conversational assistant designed to provide general information on Global Talent and Employee Mobility. It uses artificial intelligence to generate responses to user input based on trained data models.
                  </p>
                  
                  <p className="mb-3">
                    <strong>2. Eligibility</strong><br/>
                    To use the Service, you must be:<br/>
                    • At least 13 years old (16 in some jurisdictions like the EU).<br/>
                    • If under the age of majority, you must have parental or legal guardian consent.<br/>
                    • Capable of entering into a binding agreement.
                  </p>
                  
                  <p className="mb-3">
                    <strong>3. User Responsibilities</strong><br/>
                    By using the Service, you agree to:<br/>
                    • Provide accurate and lawful input.<br/>
                    • Not use the Service for unlawful, harmful, or malicious purposes.<br/>
                    • Not attempt to reverse-engineer, interfere with, or disrupt the AI or supporting infrastructure.
                  </p>
                  
                  <p className="mb-3">
                    <strong>4. Privacy and Data Use</strong><br/>
                    Your privacy is important to us. By using this Service, you agree to our Privacy Policy which outlines how we collect, store, and use your data.<br/><br/>
                    The Service may collect:<br/>
                    • Your personal details (name, email etc.)<br/>
                    • Input data (your messages)<br/>
                    • Usage logs<br/>
                    • Device/browser information<br/><br/>
                    We may use this data to improve service performance, ensure security, and comply with legal obligations.
                  </p>
                  
                  <p className="mb-3">
                    <strong>5. Limitations and Disclaimers</strong><br/>
                    • <strong>Not Human Advice:</strong> The chatbot is not a licensed professional. Any information provided is for general purposes only and should not be considered legal, medical, financial, or psychological advice.<br/>
                    • <strong>No Warranty:</strong> The Service is provided "as is" without warranties of any kind.<br/>
                    • <strong>Accuracy Not Guaranteed:</strong> While we strive for accuracy, responses may be incorrect, outdated, or misleading.<br/>
                    • <strong>Use at Your Own Risk:</strong> You assume full responsibility for your use of the Service.
                  </p>
                  
                  <p className="mb-3">
                    <strong>6. Intellectual Property</strong><br/>
                    All content, trademarks, logos, and underlying technologies used in the Service are the property of IKANTEK Solutions LLP or its licensors. You may not copy, modify, distribute, or create derivative works from the Service without permission.
                  </p>
                  
                  <p className="mb-3">
                    <strong>7. Third-Party Services</strong><br/>
                    The Service may integrate with third-party platforms (e.g., WhatsApp, Facebook Messenger, Slack). We are not responsible for the terms, data policies, or practices of those platforms.
                  </p>
                  
                  <p className="mb-3">
                    <strong>8. Termination</strong><br/>
                    We reserve the right to terminate or suspend access to the Service at any time without prior notice for violations of these Terms or if required by law.
                  </p>
                  
                  <p className="mb-3">
                    <strong>9. Modifications</strong><br/>
                    We may update these Terms from time to time. The latest version will always be available on our website or app, and your continued use after changes constitutes acceptance.
                  </p>
                  
                  <p className="mb-3">
                    <strong>10. Governing Law</strong><br/>
                    These Terms shall be governed by and construed in accordance with the laws of Government of India, without regard to conflict of law principles.
                  </p>
                  
                  <p className="mb-3">
                    <strong>11. Contact Us</strong><br/>
                    If you have questions or concerns about these Terms, please contact us at:<br/>
                    <strong>Email:</strong> vinod@ikan.com or enquiry@ikan.com<br/>
                    <strong>Address:</strong> Vinod Sharma, IKANTEK Solutions LLP., 723, Seventh Floor, DLF Tower A, Jasola, New Delhi 110025, India
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Privacy Policy Popup */}
          {showPrivacyPopup && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white pt-[20px] pb-[16px] rounded-lg shadow-lg w-11/12 max-w-[600px] h-[70vh] z-[1003] border border-[#164451] overflow-y-auto custom-scrollbar">
              <div className="flex justify-between px-4 items-center mb-4">
                <div></div>
                <button className="" onClick={() => setShowPrivacyPopup(false)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 1.5C6.15 1.5 1.5 6.15 1.5 12C1.5 17.85 6.15 22.5 12 22.5C17.85 22.5 22.5 17.85 22.5 12C22.5 6.15 17.85 1.5 12 1.5ZM16.05 17.25L12 13.2L7.95 17.25L6.75 16.05L10.8 12L6.75 7.95L7.95 6.75L12 10.8L16.05 6.75L17.25 7.95L13.2 12L17.25 16.05L16.05 17.25Z"
                      fill="#FF0000"
                    />
                  </svg>
                </button>
              </div>
              <div className="px-4">
                <div className="terms-content">
                  <h4 className="text-lg font-semibold mb-3">Privacy Policy for IKANTEK's AI Chatbot – AIRA</h4>
                  <p className="text-sm text-gray-600 mb-4"><strong>Effective Date:</strong> 10th June 2025</p>
                  
                  <p className="mb-3">
                    At IKANTEK Solutions LLP ("we", "us", or "our"), we are committed to protecting your privacy and handling your personal data responsibly. This Privacy Policy outlines how we collect, use, share, and protect your information when you interact with our AI chatbot, in compliance with international data protection laws.
                  </p>
                  
                  <p className="mb-3">
                    <strong>1. Scope and Applicability</strong><br/>
                    This policy applies to all users of our AI chatbot, regardless of their location. We comply with applicable data privacy laws, including but not limited to:<br/>
                    • EU General Data Protection Regulation (GDPR)<br/>
                    • UK General Data Protection Regulation (UK-GDPR)<br/>
                    • California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA)<br/>
                    • Brazil's Lei Geral de Proteção de Dados (LGPD)<br/>
                    • Canada's Personal Information Protection and Electronic Documents Act (PIPEDA)<br/>
                    • South Africa's Protection of Personal Information Act (POPIA)<br/>
                    • India's Digital Personal Data Protection (DPDP) Act<br/>
                    • Singapore's Personal Data Protection Act (PDPA)<br/>
                    • Australia's Privacy Act<br/>
                    • Other relevant local privacy laws
                  </p>
                  
                  <p className="mb-3">
                    <strong>2. Information We Collect</strong><br/>
                    We collect the following types of information:<br/><br/>
                    <strong>a. Personal Data (Voluntary)</strong><br/>
                    • Name, email, phone number, or other identifiers you provide<br/>
                    • Any other personal data entered in the conversation<br/><br/>
                    <strong>b. Chat and Interaction Data</strong><br/>
                    • Messages you send and receive<br/>
                    • Conversation context and timestamps<br/><br/>
                    <strong>c. Technical and Usage Data</strong><br/>
                    • IP address, device type, browser type, OS<br/>
                    • Session duration, clickstream data, chatbot usage patterns<br/><br/>
                    <strong>d. Cookies and Tracking</strong><br/>
                    We may use cookies or similar technologies to collect data about your interaction with our service. Where required, we seek your consent before using non-essential cookies.
                  </p>
                  
                  <p className="mb-3">
                    <strong>3. Legal Basis for Processing</strong><br/>
                    Depending on your jurisdiction, our legal bases for processing your data may include:<br/>
                    • Consent – You voluntarily provide your information<br/>
                    • Performance of a Contract – To deliver chatbot services<br/>
                    • Legal Obligation – To comply with applicable laws<br/>
                    • Legitimate Interests – To improve services and ensure security<br/><br/>
                    You can withdraw your consent at any time by contacting us at enquiry@ikan.com
                  </p>
                  
                  <p className="mb-3">
                    <strong>4. How We Use Your Information</strong><br/>
                    We may use your information to:<br/>
                    • Provide chatbot services and respond to your queries<br/>
                    • Improve functionality, accuracy, and user experience<br/>
                    • Analyze usage trends and system performance<br/>
                    • Prevent abuse, fraud, or security threats<br/>
                    • Comply with legal obligations
                  </p>
                  
                  <p className="mb-3">
                    <strong>5. Data Sharing and Disclosure</strong><br/>
                    We do not sell your personal information. We may share data:<br/>
                    • With trusted third-party processors (e.g., hosting, analytics providers) under strict confidentiality<br/>
                    • With regulatory or legal authorities when required<br/>
                    • With your consent, for optional integrations or features<br/><br/>
                    All processors and sub-processors are contractually bound to data protection terms compliant with applicable laws.
                  </p>
                  
                  <p className="mb-3">
                    <strong>6. International Data Transfers</strong><br/>
                    If your data is transferred outside your country (to servers in the US or EU), we ensure appropriate safeguards such as:<br/>
                    • Standard Contractual Clauses (SCCs)<br/>
                    • Data Processing Agreements (DPAs)<br/>
                    • Adequacy decisions where applicable (e.g., UK to EU)
                  </p>
                  
                  <p className="mb-3">
                    <strong>7. Data Retention</strong><br/>
                    We retain personal and interaction data only as long as necessary for the purposes described or as required by law. You may request deletion of your data anytime.
                  </p>
                  
                  <p className="mb-3">
                    <strong>8. Your Privacy Rights</strong><br/>
                    Depending on your location, your rights may include:<br/><br/>
                    <strong>Right</strong> | <strong>Description</strong><br/>
                    <strong>Access</strong> | Request a copy of your personal data<br/>
                    <strong>Rectification</strong> | Correct inaccurate or incomplete data<br/>
                    <strong>Erasure</strong> | Request deletion ("Right to be forgotten")<br/>
                    <strong>Restriction</strong> | Limit processing of your data<br/>
                    <strong>Objection</strong> | Object to processing based on legitimate interests<br/>
                    <strong>Portability</strong> | Receive your data in a machine-readable format<br/>
                    <strong>Withdraw Consent</strong> | At any time, without affecting prior lawful processing<br/>
                    <strong>Non-Discrimination</strong> | Exercise rights without facing discrimination (CCPA)<br/><br/>
                    To exercise your rights, please contact us at: enquiry@ikan.com
                  </p>
                  
                  <p className="mb-3">
                    <strong>9. Children's Privacy</strong><br/>
                    This service is not intended for individuals under the age of 13 (or applicable age of digital consent). We do not knowingly collect data from children without parental consent.
                  </p>
                  
                  <p className="mb-3">
                    <strong>10. Data Security</strong><br/>
                    We implement technical and organizational security measures, including:<br/>
                    • Encryption of data in transit and at rest<br/>
                    • Access control and authentication<br/>
                    • Regular monitoring and vulnerability assessments<br/><br/>
                    While we strive to protect your data, no system is 100% secure.
                  </p>
                  
                  <p className="mb-3">
                    <strong>11. Changes to This Policy</strong><br/>
                    We may update this policy from time to time. Any changes will be posted on this page with a revised "Effective Date." Continued use of the chatbot indicates acceptance.
                  </p>
                  
                  <p className="mb-3">
                    <strong>12. Contact Us</strong><br/>
                    If you have questions or concerns about your data or this policy, please contact:<br/><br/>
                    <strong>IKANTEK Solutions LLP</strong><br/>
                    <strong>Email:</strong> enquiry@ikan.com / vinod@ikan.com<br/>
                    <strong>Address:</strong> 723, Seventh Floor, DLF Tower A, Jasola, New Delhi 110025, INDIA
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* User Login section else Chat section*/}
          {!showChat && !showOTP ? (
            <div className="px-3 flex flex-col flex-grow overflow-y-auto">
              <div className="flex-grow">
                <h3 className="intro-heading text-left mb-1">Let's Talk</h3>
                <p className="sub-text text-sm text-left mb-4">
                  Please provide some information to help us begin our
                  conversation.
                </p>

                {/* Name */}
                <div className="flex justify-between items-center mb-1.5">
                  <label className="label-text">Name</label>
                  {errors.name && (
                    <span className="error-text text-xs text-red-500 italic">
                      *{errors.name}
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  value={userInfo.name}
                  className="user-input-field w-full py-1 px-3 border rounded-lg focus:ring-1 focus:ring-[#134550]"
                  onChange={(e) =>
                    setUserInfo({ ...userInfo, name: e.target.value })
                  }
                />

                {/* Email */}
                <div className="flex justify-between items-center mb-1.5 mt-3">
                  <label className="label-text">Email</label>
                  {errors.email && (
                    <span className="error-text text-xs text-red-500 italic">
                      *{errors.email}
                    </span>
                  )}
                </div>
                <input
                  type="email"
                  value={userInfo.email}
                  className="user-input-field w-full py-1 px-3 border rounded-lg focus:ring-1 focus:ring-[#134550]"
                  onChange={(e) =>
                    setUserInfo({ ...userInfo, email: e.target.value })
                  }
                />

                {/* Country */}
                <div className="flex justify-between items-center mb-1.5 mt-3">
                  <label className="label-text">Destination Country</label>
                  {errors.country && (
                    <span className="error-text text-xs text-red-500 italic">
                      *{errors.country}
                    </span>
                  )}
                </div>
                {/* Dropdown implementation */}
                <div ref={countryDropdownRef} className="relative">
                  <button
                    type="button"
                    className="user-input-field w-full py-2 px-3 border rounded-lg focus:ring-1 focus:ring-[#134550] flex items-center justify-between"
                    onClick={handleCountryFieldClick}
                  >
                    <span className={userInfo.country ? 'text-gray-900' : 'text-gray-500'}>
                      {userInfo.country || 'Select destination country'}
                    </span>
                    <i
                      className={`fa-solid fa-chevron-${
                        isCountryDropdownOpen ? 'up' : 'down'
                      } text-gray-500 ml-2`}
                    ></i>
                  </button>
                  {isCountryDropdownOpen && (
                    <div className="absolute z-50 mt-2 w-full bg-white border border-[#134550] rounded-lg shadow-lg">
                      <div className="p-2 border-b border-[#e0e0e0]">
                        <input
                          ref={countrySearchInputRef}
                          type="text"
                          value={countrySearchTerm}
                          onChange={(e) => setCountrySearchTerm(e.target.value)}
                          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-[#134550]"
                          placeholder="Search country..."
                        />
                      </div>
                      <div className="max-h-52 overflow-y-auto custom-scrollbar">
                        {filteredCountries.length > 0 ? (
                          filteredCountries.map((countryName) => (
                            <button
                              type="button"
                              key={countryName}
                              className="w-full text-left px-3 py-2 hover:bg-[#EFF6F8] focus:bg-[#E0EEF2]"
                              onClick={() => handleCountrySelect(countryName)}
                            >
                              {countryName}
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            No countries found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <label className="block label-text mb-1.5 mt-3">
                  Please select one:
                </label>
                <div className="space-y-3 mb-4">
                  <label className="flex items-center label-text">
                    <input
                      type="radio"
                      name="relocationType"
                      value="self"
                      checked={userInfo.isManagingRelocation === false}
                      onChange={() =>
                        setUserInfo({ ...userInfo, isManagingRelocation: false })
                      }
                      className="mr-2 label-text"
                    />
                    Are you relocating?
                  </label>
                  <label className="flex items-center label-text">
                    <input
                      type="radio"
                      name="relocationType"
                      value="managing"
                      checked={userInfo.isManagingRelocation === true}
                      onChange={() =>
                        setUserInfo({ ...userInfo, isManagingRelocation: true })
                      }
                      className="mr-2"
                    />
                    Are you managing someone's relocation?
                  </label>
                </div>

                <div className="flex items-center mb-1.5 mt-1.5">
                  <input
                    type="checkbox"
                    className="mr-2 focus:outline-1"
                    checked={acceptedTerms}
                    onChange={() => setAcceptedTerms(!acceptedTerms)}
                  />
                  <span className="check-box-text">
                    I accept{' '}
                    <span className="check-box-link cursor-pointer" onClick={() => setShowTermsPopup(true)}>Terms & Conditions</span> and{' '}
                    <span className="check-box-link cursor-pointer" onClick={() => setShowPrivacyPopup(true)}>Privacy Policy</span>
                  </span>
                </div>
              </div>
              <div className="mt-auto p-3">
                <button
                  className="w-full start-button p-2.5 rounded-lg transition duration-300"
                  onClick={handleStart}
                  disabled={isStarting}
                >
                  {isStarting ? (
                    <span className="flex items-center justify-center">
                      <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                      Generating OTP
                    </span>
                  ) : (
                    'START'
                  )}
                </button>
              </div>
            </div>
          ) : showOTP ? (
            <div className="px-3 flex flex-col flex-grow overflow-y-auto">
                          <OTPVerification
              userEmail={userInfo.email}
              onBack={handleOTPBack}
              onVerify={handleOTPVerify}
              onResendOTP={handleOTPResend}
              otpResponse={JSON.parse(localStorage.getItem('otpResponse') || '{}')}
              isOtpSending={isStarting}
              onVerificationSuccess={handleOTPVerificationSuccess}
            />
            </div>
          ) : (
            <div
              ref={chatContainerRef}
              className="flex flex-col flex-grow overflow-y-auto px-4 py-3 space-y-2 mt-[14px]"
            >
              {messages.filter((m) => !m.hidden).map((msg, index) => (
                <div
                  key={index}
                  // style={{ whiteSpace: 'pre-line' }}
                  className={`flex ${
                    msg.sender === 'bot' ? 'justify-start' : 'justify-end'
                  }`}
                >
                  {msg.sender === 'bot' && (
                    <div className="flex items-end mr-1">
                      <img
                        src={chatbot_icon}
                        alt="Bot Icon"
                        className="w-[25px] h-[25px]"
                      />
                    </div>
                  )}
                  <div
                    className={`p-2 ${
                      msg.sender === 'bot' ? 'bot-text' : 'user-text'
                    }`}
                    style={msg.preLine ? { whiteSpace: 'pre-line' } : {}}
                  >
                    <ReactMarkdown
                      className="markdown-message"
                      children={msg.text}
                      remarkPlugins={[remarkGfm, remarkBreaks]}
                    />
                    {/* Show sources if available */}

                    {msg.sender === 'bot' &&
                      msg.sources &&
                      msg.sources.length > 0 && (
                        <div
                          className="source-links"
                          style={{ marginTop: '10px' }}
                        >
                          {/* Source Button and Toggle */}
                          <SourcesLink sources={msg.sources} />
                        </div>
                      )}
                  </div>
                </div>
              ))}
              {showStarterQuestions && (
                <div className="p-4 starter-question-wrapper">
                  <div className="starter-question-heading mb-2">
                    Kick-start your chat with:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {translatedStarterQuestions.map((q, index) => (
                      <button
                        key={index}
                        className="starter-question-button px-3 py-1 rounded-full hover:bg-[#e6e6e6] transition"
                        onClick={() => {
                          handleFAQQuestionSelect(q);
                          setShowStarterQuestions(false);
                        }}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Typing Indicator */}
              {isTyping && (
                <div className="typing-indicator">Thinking ...</div>
              )}{' '}
              {/* Typing Animation */}
            </div>
          )}
          {/* Input Box */}
          {showChat && (
            <div className="p-3 flex items-center">
              <i className="fa-solid fa-paperclip text-gray-400 mr-3 cursor-pointer"></i>
              <input
                type="text"
                className="user-input-msg flex-grow p-2 border rounded-lg focus:ring-1 focus:ring-[#164451]"
                placeholder="Type a message..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              />
              <button
                onClick={handleVoiceButtonClick}
                className={`ml-3 ${
                  isPaused ? 'bg-gray-400' : 'bg-[#164451]' 
                } rounded-full p-1 transition-colors duration-300 relative group`}
                title={isPaused ? `Enable IKAN's voice` : `Mute IKAN's voice`}
              >
                <i
                  className={`fa-solid fa-volume-${
                    isPaused ? 'mute' : 'high'
                  } text-white`}
                ></i>
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                  {isPaused ? `Enable IKAN's voice` : `Mute IKAN's voice`}
                </span>
              </button>
              <i
                className="fa-regular fa-paper-plane text-white bg-[#F1593F] rounded-full p-2 ml-3 cursor-pointer w-[33px] h-[33px]"
                onClick={sendMessage}
              ></i>
            </div>
          )}
          {/* Footer */}
          <p className="text-center trademark-text mb-3">
          All rights reserved @ <strong>IKANTEK Solutions LLP</strong>
          </p>
        </div>
      )}
    </div>
  );
};

export default Chatbot;
